import React, { useEffect, useState } from 'react';
import styles from './ComparisonHistory.module.scss';
import { Button } from '@zurich-primitives/button';
import { Trash } from '@zurich-data-dsm-icons/action.trash';
import { useAppSelector, getBusinessArea } from 'app/redux';
import {
  IComparison,
  IComparisonHistory,
  IComparisonHistoryResponse
} from './ComparisonHistory.interface';
import { LoadingSpinner } from 'app/components';
import { deleteComparison, getComparisonHistory } from 'integration';

export const ComparisonHistory: React.FC<IComparisonHistory> = ({
  onViewReport
}) => {
  const currentService = 'document-compare';
  const currentBusinessArea =
    useAppSelector(getBusinessArea) ?? 'cross-functional';

  const [comparisonHistory, setComparisonHistory] = useState<IComparison[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(true);
    getComparisonHistory(currentBusinessArea, currentService)
      .then((historyResponse: IComparisonHistoryResponse) => {
        setComparisonHistory(historyResponse?.compares ?? []);
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleDeleteComparison = (comparisonId: string): void => {
    const removeById = (history: IComparison[], id: string): IComparison[] =>
      history.filter((c) => c.id !== id);
    deleteComparison(currentBusinessArea, currentService, comparisonId)
      .then(() => {
        setComparisonHistory((prevHistory) =>
          removeById(prevHistory, comparisonId)
        );
      })
      .catch((error) => {
        console.error('Error deleting comparison:', error);
      });
  };
  return (
    <>
      <div className={styles['comparison-history-container']}>
        <table className={styles['comparison-history-table']}>
          <thead>
            <tr>
              <th>Report Name</th>
              <th>Base Document</th>
              <th>Comparison Document</th>
              <th>Date Created</th>
              <th colSpan={2}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {comparisonHistory.length === 0 && (
              <tr className={styles['no-data-row']}>
                <td colSpan={6}>No data available</td>
              </tr>
            )}
            {comparisonHistory.map((comparison) => (
              <tr key={comparison.id}>
                <td>{comparison.name}</td>
                <td>{comparison.document1}</td>
                <td>{comparison.document2}</td>
                <td>{comparison.uploadDate}</td>
                <td>
                  <Button
                    size="small"
                    onClick={() => onViewReport(comparison.id)}
                  >
                    <Button.Label>View Report</Button.Label>
                  </Button>
                </td>
                <td>
                  <Button
                    onClick={() => handleDeleteComparison(comparison.id)}
                    className={styles['delete-button']}
                    aria-label="Delete comparison"
                    variant="icon"
                    size="small"
                    disabled
                  >
                    <Button.Icon>
                      <Trash />
                    </Button.Icon>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isLoading && <LoadingSpinner />}
      </div>
      <div>
        {comparisonHistory.length === 0 && (
          <div className={styles['no-data-message']}>
            You haven&apos;t made any document comparisons yet.
            <br />
            Start by uploading documents and creating your first comparison.
          </div>
        )}
      </div>
    </>
  );
};
